/**
 * DAVIDsTEA: clicking "Add" on one product card, then a different product card's "Add" before
 * the first request finishes, races two independent cart mutations against the same shared
 * cart-drawer-section morph — confirmed live to drop an item from the cart entirely and leave
 * the drawer showing a row count that didn't match the real cart. Every add-to-cart-component
 * already guards against being double-clicked itself (assets/product-form.js's
 * AddToCartComponent.disable/enable), but nothing stops a *different* button from firing while
 * the first is still in flight — there was no cross-component lock at all.
 *
 * This listens for the same cartLinesUpdate event every add/remove/quantity-change already
 * dispatches (assets/component-cart-items.js, assets/product-form.js) and disables every
 * add-to-cart button on the page for the duration of any one in-flight mutation, so a second
 * click simply can't start a second request until the first has fully resolved — the race
 * becomes impossible to trigger rather than something to resolve correctly after the fact.
 *
 * Only buttons this module itself disables are ever re-enabled: a button already disabled for
 * its own reason (sold out, no variant selected) before the lock started is left untouched,
 * so this can't accidentally make an unavailable product addable again.
 *
 *
 * The event is not the whole story, because not every cart write is the theme's.
 *
 * LoyaltyLion claims a reward with its own `POST /cart/add.js`, from a button it renders itself
 * inside `[data-lion-integrated-page]` — not an `add-to-cart-component`, so nothing above sees
 * it. The only thing this theme ever hears from that app is its `cart.changed` event, which
 * fires *after* the write has already landed (see assets/dt-loyaltylion-cart.js). So for the
 * whole duration of a redemption the page stayed unlocked, and a shopper adding a product in
 * that window hit exactly the race described above — the one already confirmed to drop an item.
 *
 * Hence the second half of this file: the lock is also held for any in-flight request to a cart
 * write route, whoever made it. Watching the network rather than the app means this covers
 * LoyaltyLion without depending on a single class name in someone else's widget, and covers the
 * next app that writes to the cart without needing to be told about it.
 *
 * Both halves share one refcount. The theme's own adds are therefore counted twice — once when
 * the request goes out, once for the event — which is harmless for a refcount and makes the lock
 * strictly longer: held from the moment the request leaves to the moment the theme has finished
 * rendering the result.
 */
import { StandardEvents } from '@shopify/events';

/** @type {number} */
let activeMutations = 0;

/** @type {Set<Element>} */
let lockedButtons = new Set();

function lockAllAddButtons() {
  for (const button of document.querySelectorAll('add-to-cart-component')) {
    if (lockedButtons.has(button)) continue;
    if (typeof (/** @type {any} */ (button).disable) !== 'function') continue;

    const addToCartButton = /** @type {any} */ (button).refs?.addToCartButton;
    if (!addToCartButton || addToCartButton.disabled) continue;

    /** @type {any} */ (button).disable();
    lockedButtons.add(button);
  }
}

function unlockAllAddButtons() {
  for (const button of lockedButtons) {
    if (typeof (/** @type {any} */ (button).enable) === 'function') {
      /** @type {any} */ (button).enable();
    }
  }
  lockedButtons.clear();
}

/**
 * A mutation that never settles would leave every add button disabled for the rest of the visit.
 * The event half has always had that exposure; the network half widens it to requests this theme
 * does not own, so each hold gets a deadline. Long enough that a slow cart write is never cut
 * short, short enough that a lost one does not strand the page.
 */
const MAX_HOLD_MS = 20000;

function beginMutation() {
  activeMutations += 1;
  lockAllAddButtons();

  let released = false;
  const timer = setTimeout(() => {
    if (released) return;
    console.warn('[dt-cart-add-lock] A cart request never settled; releasing the lock.');
    end();
  }, MAX_HOLD_MS);

  function end() {
    if (released) return;
    released = true;
    clearTimeout(timer);
    activeMutations = Math.max(0, activeMutations - 1);
    if (activeMutations === 0) unlockAllAddButtons();
  }

  return end;
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const promise = /** @type {any} */ (event).promise;
  if (!promise) return;

  const end = beginMutation();

  promise
    .finally(end)
    .catch((/** @type {any} */ error) => {
      if (error?.name !== 'AbortError') console.warn('[dt-cart-add-lock] Event promise rejected:', error);
    });
});

/**
 * The cart's write routes. Matched on the path alone and anchored to a `/cart/` segment so a
 * market or locale prefix (`/en-us/cart/add.js`) counts and a product called "cart-change" does
 * not. `update` carries note and attribute writes as well as quantities, which are cart writes
 * like any other and worth holding the lock for.
 */
const CART_WRITE_PATH = /(?:^|\/)cart\/(?:add|change|update|clear)(?:\.js)?$/;

/** @param {string} url */
function isCartWrite(url) {
  try {
    return CART_WRITE_PATH.test(new URL(url, window.location.origin).pathname);
  } catch {
    return false;
  }
}

/**
 * Observes cart writes without touching them.
 *
 * Both wrappers are pass-through: the original is called with the original arguments and its
 * return value is handed back untouched, so a failure here can only ever be a lock that is held
 * or released at the wrong moment — never a cart request that behaves differently. Anything
 * thrown while working out whether a request counts is swallowed for the same reason.
 */
function observeCartWrites() {
  // Guard against a double load: wrapping twice would double-count every request.
  if (/** @type {any} */ (window).__dtCartAddLockInstalled) return;
  /** @type {any} */ (window).__dtCartAddLockInstalled = true;

  const nativeFetch = window.fetch;
  if (typeof nativeFetch === 'function') {
    window.fetch = function (...args) {
      let end = null;
      try {
        const [input] = args;
        const url = typeof input === 'string' ? input : input instanceof Request ? input.url : String(input?.url ?? '');
        if (url && isCartWrite(url)) end = beginMutation();
      } catch {
        end = null;
      }

      if (!end) return nativeFetch.apply(this, args);

      let result;
      try {
        result = nativeFetch.apply(this, args);
      } catch (error) {
        end();
        throw error;
      }
      return Promise.resolve(result).finally(end);
    };
  }

  const XHR = window.XMLHttpRequest;
  if (typeof XHR === 'function' && XHR.prototype) {
    const nativeOpen = XHR.prototype.open;
    const nativeSend = XHR.prototype.send;

    XHR.prototype.open = function (method, url, ...rest) {
      try {
        /** @type {any} */ (this).__dtCartWrite = typeof url === 'string' && isCartWrite(url);
      } catch {
        /** @type {any} */ (this).__dtCartWrite = false;
      }
      return nativeOpen.call(this, method, url, ...rest);
    };

    XHR.prototype.send = function (...args) {
      if (/** @type {any} */ (this).__dtCartWrite) {
        const end = beginMutation();
        // `loadend` covers success, error and abort alike, so the lock cannot be stranded by a
        // request that fails or is cancelled.
        this.addEventListener('loadend', end, { once: true });
      }
      return nativeSend.apply(this, args);
    };
  }
}

observeCartWrites();
